import React, { FC, useEffect, useState } from 'react'
import { FormInput, Grid, Popup, StrictInputProps } from 'semantic-ui-react'
import Datetime from 'react-datetime'
import moment from 'moment'
import Label from './Label'
import { BaseFieldProps, Field, WrappedFieldProps } from 'redux-form'
import PureDatetimeWrapper from './PureDatetimeWrapper'
import { SemanticFieldProps } from './SemanticField'
import { format, formatDateTime } from '@cbgms/base/utils/datetime'
import { useRegisterTab } from './Tabs/TabItem'

export const ancestorHasClass = (element: Element, className: string): boolean => {
  return element.classList.contains(className) || (element.parentElement && ancestorHasClass(element.parentElement, className)) || false
}

export type ViewMode = 'years' | 'months' | 'days' | 'time'

interface IOwnProps extends Omit<StrictInputProps, 'input'> {
  formatters: any
  timeFormat?: string | boolean
  dateFormat?: string | boolean
  viewMode?: ViewMode
  icon?: any
  label?: React.ReactNode
  placeholder?: string
  timeConstraints?: Datetime.TimeConstraints
  isValidDate?: (e: moment.Moment) => boolean
  tabIndex?: string
  renderDay?: Datetime.DatetimepickerProps['renderDay']
  required?: boolean
  autoComplete?: string
  maxLength?: string
  allowToday?: boolean
  additionalContent?: JSX.Element
  onOpen?(): void
  onClose?(): void
}

interface IDateTimePickerState {
  isOpen: boolean
  editing: boolean
  masked: string
}

export interface DateTimePickerProps extends IOwnProps, WrappedFieldProps {}

export type ReduxDateTimePickerProps = SemanticFieldProps<IOwnProps>

export const ReduxDateTimePicker = (props: BaseFieldProps<ReduxDateTimePickerProps> & ReduxDateTimePickerProps) => {
  useRegisterTab(props.name)

  return (
    <Field<ReduxDateTimePickerProps>
      component={DateTimePickerBasic}
      // These are need to use the date formatters when the value gets stored in/retrieved from redux
      parse={props.parse || (props.formatters && props.formatters.parse)}
      format={props.format || (props.formatters && props.formatters.format)}
      {...props}
    />
  )
}

export const DateTimePickerBasic: FC<DateTimePickerProps> = props => {
  const {
    onOpen,
    onClose,
    viewMode,
    dateFormat,
    timeFormat,
    timeConstraints,
    placeholder,
    formatters,
    icon,
    label,
    isValidDate,
    meta,
    input,
    tabIndex,
    renderDay,
    additionalContent,
    allowToday,
    ...rest
  } = props

  const [datetimePicker, setDatetimePicker] = useState<IDateTimePickerState>({
    isOpen: false,
    editing: false,
    masked: input.value || ''
  })

  const handleOpen = () => {
    setDatetimePicker({ ...datetimePicker, isOpen: true, editing: true })
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDatetimePicker({ ...datetimePicker, masked: e.target.value })

    if (input && typeof input.onChange === 'function') {
      input.onChange(e)
    }
  }

  const handleBlur = (e: React.FormEvent<HTMLInputElement>) => {
    const { toUserFormat, fromUserFormat } = formatters
    const { value, onBlur } = input
    const momentValue = fromUserFormat(value)

    setTimeout(() => {
      const target = ((e as any).explicitOriginalTarget as Element) || document.activeElement
      const shouldClose = !ancestorHasClass(target, 'datepicker')

      setDatetimePicker(prevState => ({
        ...prevState,
        editing: !shouldClose,
        isOpen: !shouldClose,
        masked: momentValue.isValid() ? toUserFormat(momentValue) : ''
      }))

      if (shouldClose && onBlur) {
        onBlur(value)
      }
    })
  }

  const handleDateChange = (value: string | moment.Moment) => {
    const formattedValue = formatters.toUserFormat(value)
    const isChangingTimeEnabled = Boolean(timeFormat)

    setDatetimePicker({
      masked: formattedValue,
      isOpen: isChangingTimeEnabled,
      editing: isChangingTimeEnabled
    })

    if (input && typeof input.onChange === 'function') {
      input.onChange(value)
    }
  }

  const error = !!meta && Boolean(meta.touched && meta.error)
  const momentValue = formatters.fromUserFormat(datetimePicker.masked)

  // The redux form value might have been updated from another component (by dispatching a redux-form action for example).
  // We need to update the mask if the underlying valuer has been updated, while the user is not editing the input.
  useEffect(() => {
    if (datetimePicker.editing) return
    setDatetimePicker(prevState => ({ ...prevState, masked: input.value }))
  }, [input.value])

  const currentDate =
    timeFormat !== false ? formatDateTime(Date.now(), format.longWeekdayDateTime) : formatDateTime(Date.now(), format.longWeekdayDate)

  return (
    <Popup
      data-testid='datepicker-popper'
      className='datepicker'
      content={
        <>
          <PureDatetimeWrapper
            timeConstraints={timeConstraints}
            dateFormat={dateFormat}
            timeFormat={timeFormat}
            input={false}
            onChange={handleDateChange}
            initialViewMode={viewMode}
            value={momentValue}
            isValidDate={isValidDate}
            renderDay={renderDay}
          />
          {additionalContent}
          {viewMode !== 'time' && (
            <Grid columns={1}>
              <Grid.Column width={16} className='mt1' textAlign='center'>
                <span
                  className={allowToday ? 'textbutton clickable' : 'textbutton rdtDisabled'}
                  onClick={() => (allowToday ? handleDateChange(moment()) : undefined)}
                >
                  {currentDate}
                </span>
              </Grid.Column>
            </Grid>
          )}
        </>
      }
      onBlur={handleBlur}
      tabIndex={tabIndex || '-1'}
      open={datetimePicker.isOpen}
      on='focus'
      onOpen={onOpen}
      onClose={onClose}
      position='top center'
      trigger={
        <FormInput
          {...input}
          value={datetimePicker.masked}
          onBlur={handleBlur}
          onFocus={handleOpen}
          onClick={handleOpen}
          onChange={handleInputChange}
          icon={icon}
          error={error}
          placeholder={placeholder === undefined ? moment.localeData().longDateFormat('L') : placeholder}
          label={<Label touched={!!meta && !!meta.touched} error={!!meta && meta.error} content={label} />}
          {...rest}
        />
      }
    />
  )
}

DateTimePickerBasic.defaultProps = {
  viewMode: 'days',
  icon: 'calendar',
  allowToday: true
}
export default DateTimePickerBasic
